// useSubscription: React hook for subscription status and management
// Provides real-time subscription data with caching and error handling

import { useState, useEffect, useCallback } from 'react'
import { billingService, BillingServiceError } from '@/services/BillingService'
import type { 
  SubscriptionStatusResponse, 
  SubscriptionPlan,
  CreateCheckoutRequest,
  CheckoutSessionResponse 
} from '@/types/billing'

interface UseSubscriptionReturn {
  // State
  subscription: SubscriptionStatusResponse | null
  plans: SubscriptionPlan[]
  isLoading: boolean
  error: string | null
  
  // Actions
  refreshSubscription: () => Promise<void>
  loadPlans: () => Promise<void>
  createCheckoutSession: (request: CreateCheckoutRequest) => Promise<CheckoutSessionResponse>
  openCustomerPortal: (returnUrl?: string) => Promise<void>

  // Helpers
  isSubscribed: boolean
  isTrial: boolean
  isPaidSubscriber: boolean
  isPremium: boolean
  canAccessFeature: (feature: string) => boolean
}

export function useSubscription(): UseSubscriptionReturn {
  const [subscription, setSubscription] = useState<SubscriptionStatusResponse | null>(null)
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load subscription status with enhanced error handling
  const refreshSubscription = useCallback(async () => {
    try {
      setError(null)
      const status = await billingService.getSubscriptionStatus()
      setSubscription(status)
      console.log('✅ Subscription status loaded successfully:', status)
    } catch (err) {
      console.error('⚠️ useSubscription - Error loading subscription status:', err)

      // Categorize errors for better user experience
      if (err instanceof BillingServiceError && err.isAuthError()) {
        setError('Authentication required - please log in to view subscription status')
      } else if (err instanceof BillingServiceError && err.statusCode >= 500) {
        setError('Server temporarily unavailable - please try again in a moment')
      } else {
        setError('Unable to load subscription data - please refresh the page')
      }

      // REMOVED: Dangerous fallback that masks subscription issues
      // DO NOT set fake trial state - let the error surface properly
      // The UI should handle null subscription state gracefully
      setSubscription(null)
    }
  }, [])

  // Load available plans
  const loadPlans = useCallback(async () => {
    try {
      setError(null)
      const availablePlans = await billingService.getSubscriptionPlans()
      console.log('🔍 useSubscription - Loaded plans from API:', availablePlans)
      setPlans(availablePlans)
    } catch (err) {
      console.error('Failed to load subscription plans from API:', err)
      console.log('🔍 useSubscription - Using fallback plans from STRIPE_PRICES config')

      // Fallback: Create plans from STRIPE_PRICES config
      const fallbackPlans = [
        {
          id: 'starter',
          name: 'Starter',
          description: 'For hobby photographers',
          credits_per_month: 500,
          price_monthly_cents: 600,  // $6/month to match landing page
          price_annual_cents: 5800,  // $58/year (~17% discount)
          is_active: true,
          is_popular: false,
          sort_order: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: 'professional',
          name: 'Professional',
          description: 'For serious photographers',
          credits_per_month: 2000,
          price_monthly_cents: 1600,  // $16/month to match landing page
          price_annual_cents: 15400,  // $154/year (~20% discount)
          is_active: true,
          is_popular: true,
          sort_order: 2,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: 'business',
          name: 'Business',
          description: 'For agencies & teams',
          credits_per_month: 10000,
          price_monthly_cents: 5000,  // $50/month to match landing page
          price_annual_cents: 48000,  // $480/year (~20% discount)
          is_active: true,
          is_popular: false,
          sort_order: 3,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ]
      // NOTE: Removed Enterprise plan to match landing page - no Enterprise option

      setPlans(fallbackPlans)
      setError(null) // Clear error since we have fallback data
    }
  }, [])

  // Create Stripe checkout session
  const createCheckoutSession = useCallback(async (request: CreateCheckoutRequest): Promise<CheckoutSessionResponse> => {
    try {
      setError(null)
      const session = await billingService.createCheckoutSession(request)
      
      // Track analytics event
      billingService.trackAnalyticsEvent({
        event_type: 'payment_start',
        metadata: {
          plan_id: request.price_id,
          billing_cycle: request.billing_cycle
        }
      })
      
      return session
    } catch (err) {
      console.error('Failed to create checkout session:', err)
      const errorMessage = err instanceof BillingServiceError 
        ? err.message 
        : 'Failed to create checkout session'
      setError(errorMessage)
      throw err
    }
  }, [])

  // Open Stripe customer portal
  const openCustomerPortal = useCallback(async (returnUrl?: string) => {
    try {
      setError(null)
      const portal = await billingService.createCustomerPortal(returnUrl)
      window.location.href = portal.portal_url
    } catch (err) {
      console.error('Failed to open customer portal:', err)
      const errorMessage = err instanceof BillingServiceError 
        ? err.message 
        : 'Failed to open customer portal'
      setError(errorMessage)
      throw err
    }
  }, [])

  // Check if user is on trial (has access to basic features)
  const isTrial = subscription?.status === 'trial'

  // Check if user has active paid subscription
  const isPaidSubscriber = subscription?.status === 'active'

  // Check if user has any subscription access (trial or paid)
  const isSubscribed = isPaidSubscriber || isTrial

  // Check if user has premium features (active paid subscription)
  // Note: Business plan is now the highest tier
  const isPremium = isPaidSubscriber && subscription?.plan_name === 'Business'

  // Check if user can access specific features
  const canAccessFeature = useCallback((feature: string): boolean => {
    if (!subscription) return false

    switch (feature) {
      case 'ai_processing':
        return subscription.credits_balance > 0
      case 'batch_processing':
        return isSubscribed || (subscription.credits_balance >= 5)
      case 'unlimited_processing':
        return isPaidSubscriber // Only paid subscribers get unlimited
      case 'priority_support':
        return isPaidSubscriber && ['Professional', 'Business'].includes(subscription.plan_name || '')
      case 'api_access':
        return subscription.plan_name === 'Business'
      case 'white_label':
        return subscription.plan_name === 'Business'
      case 'billing_portal':
        return isPaidSubscriber // Only paid subscribers access Stripe portal
      default:
        return true
    }
  }, [subscription, isSubscribed, isPaidSubscriber, isPremium])

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        await Promise.all([
          refreshSubscription(),
          loadPlans()
        ])
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [refreshSubscription, loadPlans])

  return {
    // State
    subscription,
    plans,
    isLoading,
    error,
    
    // Actions
    refreshSubscription,
    loadPlans,
    createCheckoutSession,
    openCustomerPortal,
    
    // Helpers
    isSubscribed,
    isTrial,
    isPaidSubscriber,
    isPremium,
    canAccessFeature,
  }
}

export default useSubscription